import { Bookmark } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { StudySection } from "./types";
import { notifySavedQuestion, savedQuestionState } from "./saved-state";
import "./study-actions.css";

export function SaveQuestionButton({ questionId, releaseId, section, domain, initiallySaved }: { questionId: string; releaseId: string; section: StudySection; domain?: string; initiallySaved?: boolean }) {
  // A different question always receives a fresh state machine, including in result review.
  return <SavedControl key={questionId} questionId={questionId} releaseId={releaseId} section={section} domain={domain} initiallySaved={initiallySaved} />;
}
function SavedControl({ questionId, releaseId, section, domain, initiallySaved }: { questionId: string; releaseId: string; section: StudySection; domain?: string; initiallySaved?: boolean }) {
  const [saved, setSaved] = useState<boolean | undefined>(() => savedQuestionState(questionId) ?? initiallySaved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const revision = useRef(0);
  const pending = useRef(false);
  const initialKnown = useRef(saved !== undefined);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ questionId: string; saved: boolean }>).detail;
      if (detail?.questionId !== questionId || typeof detail.saved !== "boolean") return;
      revision.current++;
      setSaved(detail.saved); setError("");
    };
    window.addEventListener("sat:study-saved", listener);
    return () => window.removeEventListener("sat:study-saved", listener);
  }, [questionId]);
  useEffect(() => {
    if (initialKnown.current && retry === 0) return;
    const controller = new AbortController();
    const requestRevision = revision.current;
    void api<{ saved: boolean }>(`/api/study/saved/${encodeURIComponent(questionId)}`, { signal: controller.signal }).then((value) => {
      if (!controller.signal.aborted && revision.current === requestRevision) notifySavedQuestion(questionId, value.saved);
    }).catch((reason) => { if (!controller.signal.aborted && revision.current === requestRevision) setError((reason as Error).message); });
    return () => controller.abort();
  }, [questionId, retry]);
  async function toggle() {
    if (pending.current || saved === undefined) return;
    pending.current = true; revision.current++; setBusy(true); setError("");
    try {
      const value = saved
        ? await api<{ saved: boolean }>(`/api/study/saved/${encodeURIComponent(questionId)}`, { method: "DELETE" })
        : await api<{ saved: boolean }>("/api/study/saved", { method: "POST", body: JSON.stringify({ questionId, releaseId, section, domain }) });
      notifySavedQuestion(questionId, value.saved);
    } catch (reason) { setError((reason as Error).message); }
    finally { pending.current = false; setBusy(false); }
  }
  return <span className="study-save-control"><button type="button" className={`study-save-button ${saved ? "is-saved" : ""}`} aria-pressed={saved === true} aria-label={saved ? "Remove question from saved questions" : "Save question for later"} disabled={busy || saved === undefined} onClick={() => void toggle()}><Bookmark size={17} fill={saved ? "currentColor" : "none"} /><span>{saved ? "Saved" : "Save"}</span></button>{error && <small role="alert">{error}{saved === undefined && <button type="button" onClick={() => { setError(""); setRetry((value) => value + 1); }}>Retry</button>}</small>}</span>;
}
